import React, { useEffect, useState, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiArrowLeft, FiLink } from 'react-icons/fi';
import {
  Employee, listEmployees, createEmployee, updateEmployee, deleteEmployee, linkEmployeeUser, PROVINCES, FREQUENCIES,
} from './payrollApi';
import { setFormDirty, registerSaveHandler, confirmDiscardChanges } from '../../lib/unsavedChanges';

const BLANK: Partial<Employee> = {
  first_name: '', last_name: '', province_of_employment: 'ON', status: 'active',
  wage_type: 'hourly', hourly_rate: 0, annual_salary: 0, standard_hours: 80,
  pay_frequency: 'biweekly', vacation_pct: 4, vacation_mode: 'accrue',
  additional_tax: 0, cpp_exempt: 0, ei_exempt: 0, pay_method: 'cheque',
};

/** Mask all but the last 3 SIN digits for the list. */
const maskSin = (s?: string) => (s ? `•••-•••-${String(s).replace(/\D/g, '').slice(-3)}` : '—');
const money = (n: any) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function EmployeesTab() {
  const [rows, setRows] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<Partial<Employee> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows(await listEmployees()); }
    catch (e: any) { setError(e.message || 'Could not load employees'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (): Promise<boolean> => {
    if (!editing) return true;
    if (!editing.first_name?.trim() || !editing.last_name?.trim()) { setError('First and last name are required.'); return false; }
    setSaving(true); setError('');
    try {
      if (editing.pay_employee_id) await updateEmployee(editing.pay_employee_id, editing);
      else await createEmployee(editing);
      setFormDirty(false);
      setEditing(null);
      await load();
      return true;
    } catch (e: any) { setError(e.message || 'Could not save employee'); return false; }
    finally { setSaving(false); }
  }, [editing, load]);

  // Register the save handler so the global "unsaved changes" modal can Save & continue.
  useEffect(() => {
    if (editing) { registerSaveHandler(save); return () => registerSaveHandler(null); }
  }, [editing, save]);

  const startEdit = (e?: Employee) => { setEditing(e ? { ...e } : { ...BLANK }); setFormDirty(false); };
  const change = (patch: Partial<Employee>) => { setEditing((p) => ({ ...(p || {}), ...patch })); setFormDirty(true); };

  const back = async () => { if (await confirmDiscardChanges()) { setFormDirty(false); setEditing(null); } };

  const remove = async (e: Employee) => {
    if (!window.confirm(`Remove ${e.first_name} ${e.last_name}? (soft delete — payroll history is kept)`)) return;
    try { await deleteEmployee(e.pay_employee_id); await load(); }
    catch (err: any) { setError(err.message || 'Could not delete'); }
  };

  const linkLogin = async (e: Employee) => {
    const email = window.prompt(`Link ${e.first_name} ${e.last_name} to an Akountant login by email.\nTheir pay stubs will then appear in that account's Documents.\n(The person must already have signed up.)`, e.email || '');
    if (!email) return;
    try { await linkEmployeeUser(e.pay_employee_id, email.trim()); await load(); alert('Linked — future pay stubs will appear in their own Documents.'); }
    catch (err: any) { setError(err.message || 'Could not link'); }
  };

  if (editing) {
    const isHourly = editing.wage_type !== 'salary';
    return (
      <div className="pay-card" onKeyDown={(ev) => { if (ev.key === 'Escape') back(); }}>
        <div className="pay-toolbar">
          <button className="pay-btn ghost sm" onClick={back}><FiArrowLeft /> Back</button>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>{editing.pay_employee_id ? 'Edit' : 'New'} employee</h2>
        </div>
        {error && <div className="pay-error">{error}</div>}
        <div className="pay-grid">
          <div className="pay-field"><label>First name *</label><input autoFocus value={editing.first_name || ''} onChange={(e) => change({ first_name: e.target.value })} /></div>
          <div className="pay-field"><label>Last name *</label><input value={editing.last_name || ''} onChange={(e) => change({ last_name: e.target.value })} /></div>
          <div className="pay-field"><label>Employee #</label><input value={editing.employee_no || ''} onChange={(e) => change({ employee_no: e.target.value })} /></div>
          <div className="pay-field"><label>SIN</label><input value={editing.sin || ''} placeholder="000-000-000" onChange={(e) => change({ sin: e.target.value })} /></div>
          <div className="pay-field"><label>Email</label><input value={editing.email || ''} onChange={(e) => change({ email: e.target.value })} /></div>
          <div className="pay-field"><label>Phone</label><input value={editing.phone || ''} onChange={(e) => change({ phone: e.target.value })} /></div>
          <div className="pay-field"><label>Province of employment</label>
            <select value={editing.province_of_employment} onChange={(e) => change({ province_of_employment: e.target.value })}>
              {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="pay-field"><label>Hire date</label><input type="date" value={editing.hire_date?.slice(0, 10) || ''} onChange={(e) => change({ hire_date: e.target.value })} /></div>
          <div className="pay-field"><label>Status</label>
            <select value={editing.status} onChange={(e) => change({ status: e.target.value })}>
              <option value="active">Active</option><option value="on_leave">On leave</option><option value="terminated">Terminated</option>
            </select>
          </div>

          <div className="pay-field"><label>Pay type</label>
            <select value={editing.wage_type} onChange={(e) => change({ wage_type: e.target.value as any })}>
              <option value="hourly">Hourly</option><option value="salary">Salary</option>
            </select>
          </div>
          {isHourly
            ? <>
                <div className="pay-field"><label>Hourly rate ($)</label><input type="number" step="0.01" value={editing.hourly_rate ?? 0} onChange={(e) => change({ hourly_rate: Number(e.target.value) })} /></div>
                <div className="pay-field"><label>Standard hours / period</label><input type="number" step="0.5" value={editing.standard_hours ?? 0} onChange={(e) => change({ standard_hours: Number(e.target.value) })} /></div>
              </>
            : <div className="pay-field"><label>Annual salary ($)</label><input type="number" step="0.01" value={editing.annual_salary ?? 0} onChange={(e) => change({ annual_salary: Number(e.target.value) })} /></div>}
          <div className="pay-field"><label>Pay frequency</label>
            <select value={editing.pay_frequency} onChange={(e) => change({ pay_frequency: e.target.value as any })}>
              {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div className="pay-field"><label>Vacation %</label><input type="number" step="0.1" value={editing.vacation_pct ?? 4} onChange={(e) => change({ vacation_pct: Number(e.target.value) })} /></div>
          <div className="pay-field"><label>Vacation handling</label>
            <select value={editing.vacation_mode} onChange={(e) => change({ vacation_mode: e.target.value as any })}>
              <option value="accrue">Accrue to liability</option><option value="payout">Pay each cheque</option>
            </select>
          </div>

          <div className="pay-field"><label>TD1 federal claim ($)</label><input type="number" step="0.01" placeholder="Basic personal amount" value={editing.td1_federal ?? ''} onChange={(e) => change({ td1_federal: e.target.value === '' ? null : Number(e.target.value) })} /></div>
          <div className="pay-field"><label>TD1 provincial claim ($)</label><input type="number" step="0.01" placeholder="Basic personal amount" value={editing.td1_provincial ?? ''} onChange={(e) => change({ td1_provincial: e.target.value === '' ? null : Number(e.target.value) })} /></div>
          <div className="pay-field"><label>Extra tax / cheque ($)</label><input type="number" step="0.01" value={editing.additional_tax ?? 0} onChange={(e) => change({ additional_tax: Number(e.target.value) })} /></div>
          <div className="pay-field"><label>Pay method</label>
            <select value={editing.pay_method} onChange={(e) => change({ pay_method: e.target.value })}>
              <option value="cheque">Cheque</option><option value="eft">Direct deposit (EFT)</option>
            </select>
          </div>
          <div className="pay-field"><label>CPP exempt</label>
            <select value={editing.cpp_exempt ?? 0} onChange={(e) => change({ cpp_exempt: Number(e.target.value) })}><option value={0}>No</option><option value={1}>Yes</option></select>
          </div>
          <div className="pay-field"><label>EI exempt</label>
            <select value={editing.ei_exempt ?? 0} onChange={(e) => change({ ei_exempt: Number(e.target.value) })}><option value={0}>No</option><option value={1}>Yes</option></select>
          </div>
        </div>
        <div className="pay-toolbar" style={{ marginTop: 'var(--spacing-lg)', marginBottom: 0 }}>
          <button className="pay-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save employee'}</button>
          <button className="pay-btn ghost" onClick={back}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="pay-toolbar">
        <button className="pay-btn" onClick={() => startEdit()}><FiPlus /> Add employee</button>
        <span className="spacer" />
        <span style={{ color: 'var(--secondary)', fontSize: 'var(--font-size-sm)' }}>{rows.length} employee{rows.length === 1 ? '' : 's'}</span>
      </div>
      {error && <div className="pay-error">{error}</div>}
      <div className="pay-card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? <div className="pay-empty">Loading…</div>
          : rows.length === 0 ? <div className="pay-empty">No employees yet. Add your first to start running payroll.</div>
          : (
          <table className="pay-table">
            <thead><tr>
              <th>Name</th><th>Emp #</th><th>Prov</th><th>Type</th><th className="num">Rate / Salary</th><th>Frequency</th><th>SIN</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.pay_employee_id}>
                  <td>{e.first_name} {e.last_name}</td>
                  <td>{e.employee_no || '—'}</td>
                  <td>{e.province_of_employment}</td>
                  <td>{e.wage_type === 'salary' ? 'Salary' : 'Hourly'}</td>
                  <td className="num">{e.wage_type === 'salary' ? money(e.annual_salary) + '/yr' : money(e.hourly_rate) + '/hr'}</td>
                  <td>{FREQUENCIES.find((f) => f.value === e.pay_frequency)?.label.split(' ')[0] || e.pay_frequency}</td>
                  <td>{maskSin(e.sin)}</td>
                  <td><span className={`pay-badge ${e.status === 'active' ? 'posted' : 'cancelled'}`}>{e.status}</span></td>
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>
                    <button className="pay-btn ghost sm" title={e.user_id ? 'Linked to a login' : 'Link to an Akountant login'} onClick={() => linkLogin(e)} style={e.user_id ? { color: '#047857', borderColor: '#047857' } : undefined}><FiLink /></button>{' '}
                    <button className="pay-btn ghost sm" onClick={() => startEdit(e)}><FiEdit2 /></button>{' '}
                    <button className="pay-btn ghost sm" onClick={() => remove(e)}><FiTrash2 /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
